import React, { useState } from 'react';
import TextField from '@material-ui/core/TextField';
import VisibilityIcon from '@material-ui/icons/Visibility';
import VisibilityOffIcon from '@material-ui/icons/VisibilityOff';

const StandalonePasswordField = ({
  label,
  value,
  onChange,
  onPasswordChange,
  error,
  helperText,
  ...custom
}) => {
  const [showPassword, setShowPassword] = useState(false);
  const [validationResults, setValidationResults] = useState({});
  const [strength, setStrength] = useState(0);

  // Common passwords list (subset for demo)
  const commonPasswords = [
    'password', '123456', '123456789', 'qwerty', 'abc123', 'password123',
    'admin', 'letmein', 'welcome', 'monkey', '1234567890', 'iloveyou'
  ];

  // Keyboard patterns
  const keyboardPatterns = [
    'qwerty', 'qwertyui', 'qwertyuio', 'qwertyuiop', 'qwer', 'qwert', 'asdf', 'asdfg',
    'asdfgh', 'asdfghjk', 'asdfghjkl', 'zxcv', 'zxcvb', 'zxcvbn', 'zxcvbnm',
    'ytrewq', 'poiuytr', 'lkjhgf', 'mnbvcx', 'fdsa', 'gfdsa', 'hgfdsa',
    '123456', '1234567', '12345678', '123456789', '1234567890', '987654321',
    '1qaz', '2wsx', '3edc', '4rfv', '5tgb', '6yhn', '7ujm', '8ik', '9ol', '0p',
    '1qaz2wsx', '2wsx3edc', '3edc4rfv', '1qazxsw2', '2wsxcde3', '3edcvfr4'
  ];

  const passwordRules = {
    minLength: 14,
    maxLength: 128,
    requireUppercase: true,
    requireLowercase: true,
    requireNumbers: true,
    requireSpecialChars: true,
    minCharTypes: 3,
    maxRepeatingChars: 2,
    noSequentialChars: true,
    noKeyboardPatterns: true,
    noCommonPasswords: true,
    noPersonalInfo: true,
    minUppercase: 1,
    minLowercase: 1,
    minNumbers: 1,
    minSpecialChars: 1
  };

  const validatePassword = (pwd) => {
    const results = {
      length: { valid: false, message: '' },
      uppercase: { valid: false, message: '', count: 0 },
      lowercase: { valid: false, message: '', count: 0 },
      numbers: { valid: false, message: '', count: 0 },
      specialChars: { valid: false, message: '', count: 0 },
      repeatingChars: { valid: false, message: '' },
      sequentialChars: { valid: false, message: '' },
      keyboardPatterns: { valid: false, message: '' },
      commonPasswords: { valid: false, message: '' },
      characterTypes: { valid: false, message: '', count: 0 }
    };

    // Length validation
    if (pwd.length >= passwordRules.minLength && pwd.length <= passwordRules.maxLength) {
      results.length.valid = true;
      results.length.message = `✓ Length: ${pwd.length} characters`;
    } else if (pwd.length < passwordRules.minLength) {
      results.length.message = `✗ Must be at least ${passwordRules.minLength} characters`;
    } else {
      results.length.message = `✗ Must not exceed ${passwordRules.maxLength} characters`;
    }

    // Character type validation
    const uppercaseCount = (pwd.match(/[A-Z]/g) || []).length;
    const lowercaseCount = (pwd.match(/[a-z]/g) || []).length;
    const numbersCount = (pwd.match(/\d/g) || []).length;
    const specialCharsCount = (pwd.match(/[!@#$%^&*(),.?":{}|<>[\]\\\/~`_+=\-;']/g) || []).length;

    results.uppercase.count = uppercaseCount;
    results.uppercase.valid = uppercaseCount >= passwordRules.minUppercase;
    results.uppercase.message = results.uppercase.valid 
      ? `✓ Uppercase letters: ${uppercaseCount}`
      : `✗ Need at least ${passwordRules.minUppercase} uppercase letter(s)`;

    results.lowercase.count = lowercaseCount;
    results.lowercase.valid = lowercaseCount >= passwordRules.minLowercase;
    results.lowercase.message = results.lowercase.valid
      ? `✓ Lowercase letters: ${lowercaseCount}`
      : `✗ Need at least ${passwordRules.minLowercase} lowercase letter(s)`;

    results.numbers.count = numbersCount;
    results.numbers.valid = numbersCount >= passwordRules.minNumbers;
    results.numbers.message = results.numbers.valid
      ? `✓ Numbers: ${numbersCount}`
      : `✗ Need at least ${passwordRules.minNumbers} number(s)`;

    results.specialChars.count = specialCharsCount;
    results.specialChars.valid = specialCharsCount >= passwordRules.minSpecialChars;
    results.specialChars.message = results.specialChars.valid
      ? `✓ Special characters: ${specialCharsCount}`
      : `✗ Need at least ${passwordRules.minSpecialChars} special character(s)`;

    // Character type diversity
    const charTypeCount = [
      uppercaseCount > 0,
      lowercaseCount > 0,
      numbersCount > 0,
      specialCharsCount > 0
    ].filter(Boolean).length;

    results.characterTypes.count = charTypeCount;
    results.characterTypes.valid = charTypeCount >= passwordRules.minCharTypes;
    results.characterTypes.message = results.characterTypes.valid
      ? `✓ Character type diversity: ${charTypeCount}/4`
      : `✗ Must use at least ${passwordRules.minCharTypes} different character types`;

    // Repeating characters check
    const repeatingRegex = new RegExp(`(.)\\1{${passwordRules.maxRepeatingChars},}`, 'i');
    results.repeatingChars.valid = !repeatingRegex.test(pwd);
    results.repeatingChars.message = results.repeatingChars.valid
      ? '✓ No excessive character repetition'
      : `✗ Cannot have more than ${passwordRules.maxRepeatingChars} consecutive identical characters`;

    // Sequential characters check
    results.sequentialChars.valid = !hasSequentialChars(pwd);
    results.sequentialChars.message = results.sequentialChars.valid
      ? '✓ No sequential character patterns'
      : '✗ Cannot contain sequential characters (abc, 123, etc.)';

    // Keyboard patterns check
    results.keyboardPatterns.valid = !hasKeyboardPatterns(pwd);
    results.keyboardPatterns.message = results.keyboardPatterns.valid
      ? '✓ No keyboard patterns detected'
      : '✗ Cannot contain keyboard patterns (qwerty, asdf, etc.)';

    // Common passwords check
    results.commonPasswords.valid = !isCommonPassword(pwd);
    results.commonPasswords.message = results.commonPasswords.valid
      ? '✓ Not a common password'
      : '✗ This is a commonly used password';

    return results;
  };

  const hasSequentialChars = (pwd) => {
    const sequences = [
      'abcdefghijklmnopqrstuvwxyz',
      '0123456789',
      'zyxwvutsrqponmlkjihgfedcba',
      '9876543210'
    ];

    const lowerPwd = pwd.toLowerCase();
    
    for (const seq of sequences) {
      for (let i = 0; i <= seq.length - 3; i++) {
        const subseq = seq.substring(i, i + 3);
        if (lowerPwd.includes(subseq)) {
          return true;
        }
      }
    }
    return false;
  };

  const hasKeyboardPatterns = (pwd) => {
    const lowerPwd = pwd.toLowerCase();
    return keyboardPatterns.some(pattern => 
      lowerPwd.includes(pattern) || lowerPwd.includes(pattern.split('').reverse().join(''))
    );
  };

  const isCommonPassword = (pwd) => {
    const lowerPwd = pwd.toLowerCase();
    return commonPasswords.some(common => 
      lowerPwd.includes(common) || common.includes(lowerPwd)
    );
  };

  const calculateStrength = (results) => {
    const validChecks = Object.values(results).filter(result => result.valid).length;
    const totalChecks = Object.keys(results).length;
    return Math.round((validChecks / totalChecks) * 100);
  };

  const getStrengthLabel = (strength) => {
    if (strength < 20) return 'Very Weak';
    if (strength < 40) return 'Weak';
    if (strength < 60) return 'Fair';
    if (strength < 80) return 'Good';
    return 'Strong';
  };

  const getStrengthColor = (strength) => {
    if (strength < 20) return '#ff4757';
    if (strength < 40) return '#ff6b7a';
    if (strength < 60) return '#ffa502';
    if (strength < 80) return '#2ed573';
    return '#1e90ff';
  };

  const handlePasswordValidation = (passwordValue) => {
    if (passwordValue) {
      const results = validatePassword(passwordValue);
      setValidationResults(results);
      const strengthScore = calculateStrength(results);
      setStrength(strengthScore);
      
      // Call parent callback if provided
      if (onPasswordChange) {
        onPasswordChange(passwordValue, results, strengthScore);
      }
    } else {
      setValidationResults({});
      setStrength(0);
      if (onPasswordChange) {
        onPasswordChange(passwordValue, {}, 0);
      }
    }
  };

  const handleChange = (e) => {
    const newValue = e.target.value;
    handlePasswordValidation(newValue);
    if (onChange) {
      onChange(e);
    }
  };

  return (
    <div style={{ position: 'relative', width: '100%' }}>
      {/* Input Field Section */}
      <TextField
        label={label}
        type={showPassword ? 'text' : 'password'}
        value={value}
        onChange={handleChange}
        error={error}
        helperText={helperText}
        {...custom}
        InputProps={{
          style: {
            backgroundColor: "#e8f0fe",
            paddingRight: "40px"
          },
          endAdornment: (
            <button
              type="button"
              onClick={() => setShowPassword(!showPassword)}
              style={{
                position: 'absolute',
                right: '10px',
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                fontSize: '16px',
                color: '#666'
              }}
            >
              {showPassword ? <VisibilityIcon/> : <VisibilityOffIcon/>}
            </button>
          ),
        }}
      />

      {/* Password Strength Indicator - Below Input Field */}
      {Object.keys(validationResults).length > 0 && (
        <div style={{ marginTop: '10px', fontSize: '12px' }}>
          <div style={{ marginBottom: '8px' }}>
            <div style={{
              width: '100%',
              height: '4px',
              backgroundColor: '#e0e0e0',
              borderRadius: '2px',
              overflow: 'hidden'
            }}>
              <div style={{
                width: `${strength}%`,
                height: '100%',
                backgroundColor: getStrengthColor(strength),
                transition: 'all 0.3s ease'
              }} />
            </div>
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              marginTop: '4px'
            }}>
              <span style={{ color: getStrengthColor(strength), fontSize: '11px' }}>
                {getStrengthLabel(strength)}
              </span>
            </div>
          </div>
        </div>
      )}

      {/* Password Requirements - Floating Right Side */}
      {Object.keys(validationResults).length > 0 && (
        <div style={{ 
          position: 'absolute',
          top: '0',
          left: 'calc(100% + 16px)',
          width: '165px',
          fontSize: '12px',
          padding: '12px',
          backgroundColor: '#f8f9fa',
          borderRadius: '8px',
          border: '1px solid #e9ecef',
          boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
          zIndex: 1000
        }}>
          {/* Key Validation Results */}
          <div style={{ marginBottom: '8px', fontSize: '11px', fontWeight: '600', color: '#666' }}>
            Requirements
          </div>
          <div style={{ 
            display: 'flex',
            flexDirection: 'column',
            gap: '3px',
            fontSize: '10px'
          }}>
            {['length', 'uppercase', 'lowercase', 'numbers', 'specialChars', 'repeatingChars', 'sequentialChars', 'keyboardPatterns', 'commonPasswords', 'characterTypes'].map(key => (
              validationResults[key] && (
                <div key={key} style={{
                  color: validationResults[key].valid ? '#2ed573' : '#ff4757',
                  display: 'flex',
                  alignItems: 'center',
                  padding: '2px 0'
                }}>
                  <span style={{ marginRight: '6px', fontWeight: 'bold' }}>
                    {validationResults[key].valid ? '✓' : '✗'}
                  </span>
                  <span>
                    {key === 'length' && 'Min 14 chars'}
                    {key === 'uppercase' && 'Uppercase letter'}
                    {key === 'lowercase' && 'Lowercase letter'}
                    {key === 'numbers' && 'Number'}
                    {key === 'specialChars' && 'Special character'}
                    {key === 'repeatingChars' && 'No repeating chars'}
                    {key === 'sequentialChars' && 'No sequential chars'}
                    {key === 'keyboardPatterns' && 'No keyboard patterns'}
                    {key === 'commonPasswords' && 'Not common password'}
                    {key === 'characterTypes' && 'Character diversity'}
                  </span>
                </div>
              )
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default StandalonePasswordField;